import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const CTASection = () => {
  const navigate = useNavigate();

  const ctaCards = [
    {
      icon: "Briefcase",
      title: "Explore My Services",
      description: "Discover how I can help transform your business through IT engineering, consulting, and creative direction.",
      buttonText: "View Services",
      buttonVariant: "default",
      path: "/services-portfolio"
    },
    {
      icon: "FolderOpen",
      title: "View My Portfolio",
      description: "Browse through my completed projects, case studies, and creative works across technology and business.",
      buttonText: "See Portfolio",
      buttonVariant: "outline",
      path: "/project-portfolio"
    },
    {
      icon: "Mail",
      title: "Let\'s Connect",
      description: "Ready to start a conversation? Reach out to discuss your project, business needs, or mentorship opportunities.",
      buttonText: "Contact Me",
      buttonVariant: "secondary",
      path: "/contact-hub"
    }
  ];

  return (
    <section className="py-16 lg:py-24 bg-background">
      <div className="container mx-auto px-4 md:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12 lg:mb-16"
        >
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4">Ready to Work Together?</h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
            Let's explore how we can collaborate to bring your vision to life
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12 lg:mb-16">
          {ctaCards?.map((card, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1, duration: 0.6 }}
              whileHover={{ y: -8, transition: { duration: 0.3 } }}
              className="bg-card rounded-xl lg:rounded-2xl p-6 lg:p-8 border border-border hover:border-primary/50 transition-colors text-center"
            >
              <div className="w-16 h-16 md:w-20 md:h-20 rounded-xl bg-primary/10 flex items-center justify-center mx-auto mb-4 lg:mb-6">
                <Icon name={card?.icon} size={32} color="var(--color-primary)" />
              </div>
              <h3 className="text-lg md:text-xl font-semibold mb-3">{card?.title}</h3>
              <p className="text-sm md:text-base text-muted-foreground mb-6 leading-relaxed">
                {card?.description}
              </p>
              <Button
                variant={card?.buttonVariant}
                onClick={() => navigate(card?.path)}
                iconName="ArrowRight"
                iconPosition="right"
                fullWidth
              >
                {card?.buttonText}
              </Button>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background rounded-2xl lg:rounded-3xl p-8 lg:p-12 text-center border border-primary/20"
        >
          <div className="max-w-3xl mx-auto">
            <motion.div
              animate={{ 
                scale: [1, 1.1, 1],
                rotate: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
              className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-6"
            >
              <Icon name="Zap" size={40} color="var(--color-primary)" />
            </motion.div>
            <h3 className="text-2xl md:text-3xl font-bold mb-4">Let's Build Something Amazing</h3>
            <p className="text-base md:text-lg text-muted-foreground mb-8">
              Whether you need technical expertise, business guidance, or creative direction, I'm here to help turn your vision into reality.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                variant="default"
                size="lg"
                onClick={() => navigate('/contact-hub')}
                iconName="Send"
                iconPosition="right"
              >
                Start a Conversation
              </Button>
              <Button
                variant="outline"
                size="lg"
                onClick={() => navigate('/services-portfolio')}
                iconName="Briefcase"
                iconPosition="left"
              >
                Explore Services
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;